import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { supabase } from "../supabaseClient";
import strobeLogo from "../assets/images/strobelogo.svg";

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteId = searchParams.get("id") || "";
  const inviteType = searchParams.get("type") || "broker";
  const inviteEmail = searchParams.get("email") || "";
  const [status, setStatus] = useState("checking");
  const [error, setError] = useState("");

  const redirectPath = useMemo(() => {
    const params = new URLSearchParams({
      type: inviteType,
      id: inviteId,
    });
    if (inviteEmail) params.set("email", inviteEmail);
    return `/accept-invite?${params.toString()}`;
  }, [inviteEmail, inviteId, inviteType]);

  const registerPath = useMemo(() => {
    const params = new URLSearchParams({
      mode: "register",
      redirect: redirectPath,
    });
    if (inviteEmail) params.set("email", inviteEmail);
    return `/login?${params.toString()}`;
  }, [inviteEmail, redirectPath]);

  const handleUseInvitedEmail = async () => {
    await supabase.auth.signOut();
    navigate(registerPath, { replace: true });
  };

  useEffect(() => {
    let active = true;

    const acceptInvite = async () => {
      if (!inviteId || !["broker", "admin"].includes(inviteType)) {
        setStatus("error");
        setError("This invite link is missing details or is not supported.");
        return;
      }

      const { data: authData, error: authError } = await supabase.auth.getUser();
      const user = authData?.user;

      if (!active) return;

      if (authError || !user?.id) {
        setStatus("signedOut");
        return;
      }

      setStatus("accepting");

      const rpcName = inviteType === "admin" ? "accept_admin_invite" : "accept_broker_invite";
      const destination = inviteType === "admin" ? "/admin" : "/portal";

      const { error: acceptError } = await supabase.rpc(rpcName, {
        invite_id: inviteId,
      });

      if (!active) return;

      if (acceptError) {
        const message = acceptError.message || "Could not accept this invite.";
        if (message.toLowerCase().includes("invite email does not match")) {
          setStatus("emailMismatch");
          setError(message);
          return;
        }
        setStatus("error");
        setError(message);
        return;
      }

      setStatus("accepted");
      window.setTimeout(() => {
        if (active) navigate(destination, { replace: true });
      }, 900);
    };

    acceptInvite();

    return () => {
      active = false;
    };
  }, [inviteId, inviteType, navigate]);

  if (status === "signedOut") {
    return <Navigate to={registerPath} replace />;
  }

  return (
    <main className="min-h-screen bg-[#f4f5f7] px-5 py-10 text-[#1a1a1a]">
      <section className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-3xl items-center justify-center">
        <div className="w-full rounded-[28px] border border-black/10 bg-white/80 p-8 shadow-[0_24px_80px_rgba(0,0,0,0.12)] backdrop-blur-md md:p-12">
          <img src={strobeLogo} alt="Strobe Aviation" className="mb-10 h-auto w-52" />

          {(status === "checking" || status === "accepting") && (
            <div className="flex items-start gap-4">
              <Loader2 className="mt-1 h-7 w-7 animate-spin text-[#EB040D]" />
              <div>
                <h1 className="text-4xl font-semibold">Accepting Invite</h1>
                <p className="mt-3 text-lg leading-relaxed text-black/60">
                  We’re connecting your login to the {inviteType === "admin" ? "admin team" : "broker account"} that invited you.
                </p>
              </div>
            </div>
          )}

          {status === "accepted" && (
            <div className="flex items-start gap-4">
              <CheckCircle2 className="mt-1 h-7 w-7 text-[#EB040D]" />
              <div>
                <h1 className="text-4xl font-semibold">Invite Accepted</h1>
                <p className="mt-3 text-lg leading-relaxed text-black/60">
                  You’re now connected to this {inviteType === "admin" ? "admin team" : "broker account"}. Sending you to the portal.
                </p>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="flex items-start gap-4">
              <AlertCircle className="mt-1 h-7 w-7 text-[#EB040D]" />
              <div>
                <h1 className="text-4xl font-semibold">Invite Problem</h1>
                <p className="mt-3 text-lg leading-relaxed text-black/60">{error}</p>
                <p className="mt-5 text-base leading-relaxed text-black/50">
                  Make sure you are signed in with the same email address that received the invite.
                </p>
                <Link
                  to="/login"
                  className="mt-8 inline-flex rounded-2xl bg-[#EB040D] px-6 py-3 font-semibold text-white shadow-sm transition hover:bg-[#c90008]"
                >
                  Go to Login
                </Link>
              </div>
            </div>
          )}

          {status === "emailMismatch" && (
            <div className="flex items-start gap-4">
              <AlertCircle className="mt-1 h-7 w-7 text-[#EB040D]" />
              <div>
                <h1 className="text-4xl font-semibold">Invite Problem</h1>
                <p className="mt-3 text-lg leading-relaxed text-black/60">
                  This invite has to be accepted with the email address that received it
                  {inviteEmail ? `: ${inviteEmail}.` : "."}
                </p>
                <p className="mt-5 text-base leading-relaxed text-black/50">
                  If this person does not have a Strobe Aviation account yet, sign out here and create one with the invited email.
                </p>
                <button
                  type="button"
                  onClick={handleUseInvitedEmail}
                  className="mt-8 inline-flex rounded-2xl bg-[#EB040D] px-6 py-3 font-semibold text-white shadow-sm transition hover:bg-[#c90008]"
                >
                  Sign out and create account
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
